import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, MessageCircleHeart, Sparkles, Loader2 } from "lucide-react";

const templates = (name) => [
  `Assalamu alaikum${name ? ` ${name}` : ""}, thank you for connecting. I'd love to learn more about what you're seeking in marriage, in shaa Allah.`,
  `Assalamu alaikum, I appreciate your profile. Would it be alright to involve our walis as we get to know one another respectfully?`,
  `Assalamu alaikum${name ? ` ${name}` : ""}, may I ask which qualities matter most to you in a future spouse?`,
  `Jazak Allah khair for the connection. What does a blessed, peaceful marriage look like to you?`,
];

function buildPrompt(p) {
  const name = p?.firstName || p?.name || "them";
  const about = [
    p?.bio,
    p?.profession && `works as ${p.profession}`,
    p?.sect,
    p?.education,
    (p?.interests || []).length && `interests: ${p.interests.slice(0, 5).join(", ")}`,
  ].filter(Boolean).join(". ");
  return `Write ONE short, respectful, wali-friendly opening message to send to a potential marriage match named ${name} on an Islamic matrimonial app. About them: ${about || "not much shared yet"}. Begin with a salaam, mention something specific about them, keep it under 55 words, sincere and calm, and gently suggest involving the wali. Return only the message text, no preamble.`;
}

export function Icebreakers({ profile }) {
  const name = profile?.firstName || profile?.name;
  const [copied, setCopied] = useState("");
  const [aiText, setAiText] = useState("");
  const [loading, setLoading] = useState(false);
  const list = templates(name);

  const copy = async (text, key) => {
    try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1800); toast.success("Opener copied"); }
    catch { toast.error("Couldn't copy"); }
  };

  const craft = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/coach", { message: buildPrompt(profile) });
      const reply = (data.reply || data.message || data.text || "").replace(/^["“]|["”]$/g, "").trim();
      setAiText(reply || "Couldn't craft a message right now — try a suggestion below.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="icebreakers">
      <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-emerald-900"><MessageCircleHeart className="h-4 w-4 text-rose-500" /> Suggested openers</p>
      <p className="mb-3 text-xs text-slate-500">Respectful, wali-friendly ways to say salaam first.</p>

      <button onClick={craft} disabled={loading} data-testid="icebreaker-ai-btn" className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-700 to-emerald-900 px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-95 disabled:opacity-60">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 text-amber-300" />} {aiText ? "Craft another with AI Coach" : "Craft a personal opener with AI Coach"}
      </button>

      {aiText && (
        <button onClick={() => copy(aiText, "ai")} data-testid="icebreaker-ai-result" className="group mb-3 flex w-full items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-left transition-colors hover:bg-amber-100">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span className="flex-1 text-sm leading-relaxed text-slate-800">{aiText}</span>
          <span className="mt-0.5 shrink-0 text-emerald-700">{copied === "ai" ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4 opacity-60 group-hover:opacity-100" />}</span>
        </button>
      )}

      <div className="space-y-2">
        {list.map((t, i) => (
          <button key={i} onClick={() => copy(t, `t${i}`)} data-testid={`icebreaker-${i}`} className="group flex w-full items-start gap-2 rounded-xl border border-emerald-900/10 bg-mint/40 p-3 text-left transition-colors hover:bg-mint">
            <span className="flex-1 text-sm leading-relaxed text-slate-700">{t}</span>
            <span className="mt-0.5 shrink-0 text-emerald-700">{copied === `t${i}` ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4 opacity-60 group-hover:opacity-100" />}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
